import { Link } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import {
  FolderKanban,
  FileText,
  MessageSquare,
  ArrowRight,
  Plus,
  BarChart3,
  Grid3X3,
  Hammer,
} from 'lucide-react'

export default function Dashboard() {
  const { accounts } = useMsal()
  const currentUser = accounts[0]

  return (
    <div className="space-y-8">
      {/* Welcome header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100">
          Welcome back, {currentUser?.name?.split(' ')[0] || 'User'}
        </h1>
        <p className="mt-1 text-slate-400">Manage your PMO projects and generate Agile artifacts</p>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/projects" className="card glass-panel-hover group">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-blue-400" />
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
          </div>
          <h3 className="font-semibold text-slate-100">View Projects</h3>
          <p className="mt-1 text-sm text-slate-400">Browse and manage your existing projects</p>
        </Link>

        <Link to="/projects?new=true" className="relative overflow-hidden rounded-xl p-6 group">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-600 opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 to-cyan-600/50 opacity-0 group-hover:opacity-100 transition-opacity" />

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Plus className="w-6 h-6 text-white" />
              </div>
              <ArrowRight className="w-5 h-5 text-white/80 group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="font-semibold text-white">New Project</h3>
            <p className="mt-1 text-sm text-white/80">
              Create a new project from WBS/SOW documents
            </p>
          </div>
        </Link>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-slate-700/50 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-slate-400" />
            </div>
          </div>
          <h3 className="font-semibold text-slate-100">Recent Activity</h3>
          <p className="mt-1 text-sm text-slate-400">No recent activity yet</p>
        </div>
      </div>

      {/* Getting started */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
            <Hammer className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-slate-100">Getting Started with SprintForge</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-white">1</span>
            </div>
            <div>
              <h4 className="font-medium text-slate-200">Create Project</h4>
              <p className="text-sm text-slate-400 mt-1">
                Start by creating a new project and uploading your WBS and SOW
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-white">2</span>
            </div>
            <div>
              <h4 className="font-medium text-slate-200">Upload Documents</h4>
              <p className="text-sm text-slate-400 mt-1">
                Add WBS, SOW, Teams transcripts, and supporting materials
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-white">3</span>
            </div>
            <div>
              <h4 className="font-medium text-slate-200">Analyze & Generate</h4>
              <p className="text-sm text-slate-400 mt-1">
                Run gap analysis and generate stories, RACI, risk registers
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-white">4</span>
            </div>
            <div>
              <h4 className="font-medium text-slate-200">Export to Smartsheets</h4>
              <p className="text-sm text-slate-400 mt-1">
                Create a Smartsheets workspace and populate it automatically
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card glass-panel-hover">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="font-semibold text-slate-100">Project Chatbot</h3>
          </div>
          <p className="text-sm text-slate-400">
            Ask questions about your project documents. The AI assistant provides answers with
            source citations from your WBS, SOW, and transcripts.
          </p>
        </div>

        <div className="card glass-panel-hover">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="font-semibold text-slate-100">Gap Analysis</h3>
          </div>
          <p className="text-sm text-slate-400">
            AI-powered analysis identifies gaps in your project documentation, missing requirements,
            and potential risks before kickoff.
          </p>
        </div>

        <div className="card glass-panel-hover">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="font-semibold text-slate-100">Output Generation</h3>
          </div>
          <p className="text-sm text-slate-400">
            Generate user stories, taskboards, RACI matrices, risk registers, and 8 other
            Scrum/Agile artifacts as CSV files.
          </p>
        </div>

        <div className="card glass-panel-hover">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Grid3X3 className="w-5 h-5 text-green-400" />
            </div>
            <h3 className="font-semibold text-slate-100">Smartsheets Integration</h3>
          </div>
          <p className="text-sm text-slate-400">
            Connect your Smartsheets account and automatically create workspaces with populated
            sheets from your generated outputs.
          </p>
        </div>
      </div>
    </div>
  )
}
